// Inspired by Chatbot-UI and modified to fit the needs of this project
// @see https://github.com/mckaywrigley/chatbot-ui/blob/main/components/Chat/ChatMessage.tsx

import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";

import { MemoizedReactMarkdown } from "@/components/AI/chat/Markdown";
import { useSupabase } from "@/components/contexts/DatabaseSyncWrapper";
import Icon, { ICONS } from "@/components/Core/Icons";
import { CodeBlock } from "@/components/ui/codeblock";
import { IconUser } from "@/components/ui/icons";
import { cn } from "@/lib/utils";
import { useIsChatLoading } from "@/store/zustand/aiContextStore";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import { Message, useSupabaseStore } from "@/store/zustand/supabaseStore";
import { useHighlightedText } from "@/utils/searchUtils";
import { WrenchIcon } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import ContextButton from "../components/ContextButton";
import { EngSpecBlock } from "./messageBlocks/EngSpecBlock";
import { PRDBlock } from "./messageBlocks/PRDBlock";
import { ProgressBlock } from "./messageBlocks/ProgressBlock";
import { useCanvasStore } from "@/store/zustand/canvasStore";
import { useActiveMainCanvas } from "@/utils/utils";
import { FigmaBlock } from "./messageBlocks/FigmaBlock";
import { ErrorsBlock } from "./messageBlocks/ErrorsBlock";
import { useImageUploader } from "@/components/AppHeader/FileUpload";
import { EnvVariablesInputBlock } from "./messageBlocks/EnvVariablesInputBlock";
import { useUser } from "@clerk/clerk-react";
import { ReasoningChatMessage } from "./messageBlocks/ReasoningBlock";
import { SearchGroundingSourcesChatMessage } from "./messageBlocks/SearchGroundingSourcesBlock";
import { VercelDeploymentBlock } from "./messageBlocks/VercelDeploymentBlock";
import { parseMessageContentBlocks } from "./utils/messages";

export interface ChatMessageProps {
  message: Message;
  isLastMessage: boolean;
  searchQuery?: string;
}

const renderCode = ({ node, inline, className, children, ...props }) => {
  if (children.length) {
    if (children[0] == "▍") {
      return <span className="mt-1 cursor-default animate-pulse">▍</span>;
    }

    children[0] = (children[0] as string).replace("`▍`", "▍");
  }

  const match = /language-(\w+)/.exec(className || "");

  if (inline) {
    return (
      <code className={className} {...props}>
        {children}
      </code>
    );
  }

  const codeContent = String(children).replace(/\n$/, "");
  const codeKey = `${node.position.start.offset}`;

  return (
    <CodeBlock
      key={codeKey}
      language={(match && match[1]) || ""}
      value={codeContent}
      {...props}
    />
  );
};

// Memoized component to highlight search term matches in text
const HighlightedText = React.memo(
  ({ text, searchQuery }: { text: string; searchQuery?: string }) => {
    // Use our custom hook to get highlighted parts
    const parts = useHighlightedText(text, searchQuery);

    if (!searchQuery || !text) return text;

    return (
      <>
        {parts.map((part, i) => {
          if (part.isMatch) {
            return (
              <span
                key={i}
                className="bg-primary-200 text-primary-foreground font-semibold px-1 mx-0.5 rounded-sm"
              >
                {part.text}
              </span>
            );
          }
          return part.text;
        })}
      </>
    );
  }
);

export const ChatMessage = React.memo(
  ({ message, isLastMessage, searchQuery }: ChatMessageProps) => {
    // Check if this message contains the search query
    const hasSearchMatch = useMemo(() => {
      if (!searchQuery?.trim() || !message?.content) return false;
      return message.content.toLowerCase().includes(searchQuery.toLowerCase());
    }, [message?.content, searchQuery]);
    const { user } = useUser();

    const fileToContentsFromApi = useCodebaseStore(
      (state) => state.fileToContents
    );
    const setReloadFiles = useCodebaseStore((state) => state.setReloadFiles);

    const onDbSnapshot = useSupabaseStore((state) => state.onDbSnapshot);

    const isChatLoading = useIsChatLoading();

    const { supabaseClient } = useSupabase();

    const contentArray = useMemo(() => {
      const res = parseMessageContentBlocks(message);
      return res;
    }, [
      message.tool_calls,
      message.content,
      message.role,
      message.message_context,
    ]);

    const mainCanvas = useActiveMainCanvas();

    const usersForProject =
      useCanvasStore((state) => state.usersForProject)?.[
        mainCanvas?.project_id
      ] || [];
    const imageProfile = useMemo(() => {
      if (message.role !== "user") {
        return null;
      }

      const foundUser = usersForProject.find(
        (user) => user.id === message.auth0_user_id_sent
      );
      if (message.auth0_user_id_sent && !foundUser) {
        // User existed but is not found, use generic profile picutre
        return <IconUser />;
      }

      // If auth0_user_id is null, use the user's profile picture (old messages)
      const imageUrl = message.auth0_user_id_sent
        ? foundUser?.picture
        : user?.imageUrl;
      if (imageUrl) {
        return (
          <img
            src={imageUrl}
            className={"select-none focus:outline-none rounded-md h-6 w-6"}
            crossOrigin="anonymous"
          />
        );
      }

      return <IconUser />;
    }, [usersForProject, message.role, message.auth0_user_id_sent]);

    const [updatedMessage, setUpdatedMessage] = useState(false);

    const { FilePreviewItem } = useImageUploader();

    /**
     * If the message has code changes, also store the current code contents
     * so that if they change it no longer allows it to be applied
     */
    useEffect(() => {
      try {
        // AI hasn't finished streaming
        if (!supabaseClient || !message.message_context) {
          return;
        }

        if (updatedMessage || message.role !== "assistant") {
          return;
        }

        let needToChange = false,
          filesToReload = [];
        const newContents = contentArray
          .map((item, index) => {
            if (
              item.type === "aiCodeChange" &&
              fileToContentsFromApi[item.aiCodeChange.filename] === undefined
            ) {
              // @ts-expect-error TS2345
              filesToReload.push(item.aiCodeChange.filename);
              return "";
            }

            if (
              item.type === "aiCodeChange" &&
              item.aiCodeChange.edit &&
              !item.aiCodeChange.edit.oldContents
            ) {
              needToChange = true;

              const { startLine, endLine } = item.aiCodeChange.edit;

              let fileContents =
                fileToContentsFromApi[item.aiCodeChange.filename];

              item.aiCodeChange.edit["oldContents"] = fileContents
                ?.split("\n")
                .slice(
                  startLine !== undefined ? startLine - 1 : 0,
                  endLine || undefined
                )
                .join("\n");

              return (
                "<code-change-json>\n" +
                JSON.stringify(item.aiCodeChange, null, 2) +
                "</code-change-json>"
              );
            }

            return item.content;
          })
          .join("\n");

        const updateMessage = async () => {
          setUpdatedMessage(true);

          const updatedMessage: any = {
            ...message,
            content: newContents,
          };

          onDbSnapshot("aiChatMessages", {
            newItems: [updatedMessage],
          });

          await supabaseClient
            .from("ai_chat_messages")
            .update(updatedMessage)
            .eq("id", message.id);
        };

        if (filesToReload?.length) {
          setReloadFiles({ files: filesToReload });
        } else if (needToChange) {
          updateMessage();
        }
      } catch (e) {
        console.error(e);
      }
    }, [
      contentArray,
      message.message_context,
      fileToContentsFromApi,
      supabaseClient,
    ]);

    return (
      <div
        className={cn(
          "group relative mb-2 flex flex-col w-full pr-2",
          hasSearchMatch &&
            "border-l-2 border-primary-500 pl-2 rounded bg-primary-50 dark:bg-primary-n-900/10"
        )}
      >
        {/* User icon */}
        <div className="flex flex-row gap-1 items-center">
          <div
            className={cn(
              "flex h-6 w-6 shrink-0 select-none items-center justify-center rounded-md shadow",
              message.role === "user"
                ? "bg-avatar"
                : "bg-primary-0 text-primary-foreground",
              user && user.imageUrl ? "p-0" : "p-1"
            )}
          >
            {contentArray?.find((seg) => seg.toolCall) ? (
              <WrenchIcon className="w-4 h-4" />
            ) : message.role === "user" ? (
              imageProfile
            ) : (
              <Icon icon={ICONS.AI_LOGO} className="w-4 h-4 p-1" />
            )}
          </div>
          <div className="text-xs text-secondary-n-50">
            {message.role === "user"
              ? "You"
              : message.role === "assistant"
              ? "Tempo AI"
              : "Codebase"}
          </div>
        </div>
        {/* Message context (images, selected element) */}
        <div
          className={cn(
            "flex flex-row gap-3 pt-3",
            message.role !== "user" && "hidden"
          )}
        >
          {message?.message_context?.imageUrls?.map((url, index) => (
            <FilePreviewItem
              key={index}
              file={{
                url,
                name: url.split("/").pop() || url,
                type: "image",
                mimeType: "image/png",
              }}
            />
          ))}
          {message?.message_context?.files?.map((file, index) => (
            <FilePreviewItem key={index} file={file} />
          ))}
          {message?.message_context?.totalUserSelection?.map(
            (selectedElement, index) => (
              <div className="pt-2" key={index}>
                <ContextButton
                  key={index}
                  treeElementOrStoryboard={selectedElement}
                />
              </div>
            )
          )}
        </div>
        {/* Message content */}
        <div className="flex-1 flex flex-col gap-1.5 pt-1 space-y-1 overflow-hidden mt-1">
          {contentArray.map((block, index) => {
            if (block.type === "figmaInput") {
              return <FigmaBlock />;
            }

            if (block.type === "reasoning") {
              return <ReasoningChatMessage block={block} />;
            }

            if (block.type === "search-grounding-sources") {
              return <SearchGroundingSourcesChatMessage block={block} />;
            }

            if (block.toolCall) {
              return (
                <div
                  key={index}
                  className="flex items-center gap-2 text-xs font-medium text-secondary-n-400 bg-secondary-n-950 rounded-md p-2 my-1 border border-secondary-n-800 hover:border-secondary-n-700 transition-colors duration-200"
                >
                  <span>{block.content}</span>
                </div>
              );
            }

            if (
              block.type === "codeSnippet" ||
              block.type === "aiCodeChange" ||
              block.type === "aiChanges"
            ) {
              return (
                <EngSpecBlock
                  key={index}
                  block={block}
                  message={message}
                  blockIndex={index}
                  loadingAiChange={
                    isLastMessage &&
                    index === contentArray.length - 1 &&
                    isChatLoading
                  }
                />
              );
            }

            if (block.type === "prd-json") {
              return (
                <PRDBlock
                  key={index}
                  block={block}
                  message={message}
                  blockIndex={index}
                  isLastBlock={index === contentArray.length - 1}
                />
              );
            }

            if (block.type === "vercel-deployment-message") {
              return (
                <VercelDeploymentBlock
                  previewUrl={block?.parsedJson?.items?.previewUrl}
                  claimUrl={block?.parsedJson?.items?.claimUrl}
                />
              );
            }

            if (block.type === "action-progress") {
              return (
                <ProgressBlock
                  key={index}
                  progressItems={block.parsedJson?.items || []}
                />
              );
            }

            if (block.type === "dev-errors") {
              return (
                <ErrorsBlock
                  key={index}
                  errors={block.parsedJson || []}
                  isLastMessage={isLastMessage}
                />
              );
            }

            if (block.type === "env-variables-input") {
              return (
                <EnvVariablesInputBlock
                  key={index}
                  title={block.parsedJson?.title || "Set Up Your Env Variables"}
                  variables={block.parsedJson?.variables || []}
                  message={message}
                />
              );
            }

            return (
              // @ts-expect-error TS2745
              <MemoizedReactMarkdown
                key={index}
                className="text-xs"
                remarkPlugins={[remarkGfm, remarkMath]}
                components={{
                  a({ children }: { children: React.ReactNode; node: any }) {
                    return (
                      <a
                        href={children?.toString() || ""}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-400"
                      >
                        {children}
                      </a>
                    );
                  },
                  p({ children }) {
                    // Apply highlighting to string content in paragraphs
                    if (searchQuery) {
                      if (typeof children === "string") {
                        return (
                          <p className="mb-1">
                            {
                              <HighlightedText
                                text={children}
                                searchQuery={searchQuery}
                              />
                            }
                          </p>
                        );
                      } else if (Array.isArray(children)) {
                        // Handle array of children (common in Markdown parsing)
                        return (
                          <p className="mb-1">
                            {children.map((child, i) => {
                              if (typeof child === "string") {
                                return (
                                  <HighlightedText
                                    key={i}
                                    text={child}
                                    searchQuery={searchQuery}
                                  />
                                );
                              }
                              return child;
                            })}
                          </p>
                        );
                      }
                    }
                    return <p className="mb-1">{children}</p>;
                  },
                  li({ children }) {
                    // Apply highlighting to string content in list items
                    if (searchQuery) {
                      if (typeof children === "string") {
                        return (
                          <li className="mb-1">
                            {
                              <HighlightedText
                                text={children}
                                searchQuery={searchQuery}
                              />
                            }
                          </li>
                        );
                      } else if (Array.isArray(children)) {
                        // Handle array of children
                        return (
                          <li className="mb-1">
                            {children.map((child, i) => {
                              if (typeof child === "string") {
                                return (
                                  <HighlightedText
                                    text={child}
                                    searchQuery={searchQuery}
                                    key={i}
                                  />
                                );
                              }
                              return child;
                            })}
                          </li>
                        );
                      }
                    }
                    return <li className="mb-1">{children}</li>;
                  },
                  ul({ children, node }) {
                    return <ul className="list-disc pl-4">{children}</ul>;
                  },
                  ol({ children, node }) {
                    return (
                      <ol className="list-decimal pl-4 mb-2">{children}</ol>
                    );
                  },
                  // @ts-ignore
                  code: renderCode,
                }}
              >
                {block.content}
              </MemoizedReactMarkdown>
            );
          })}
        </div>
      </div>
    );
  }
);
